/**
 * DirectorsEditor — structured directors editor.
 *
 * Responsibility: CRUD for Director rows (name, title, signingAuthority).
 * Renders the live director list, add-row form, and per-row inline editing.
 *
 * Query key: ["entity-directors", entityId]
 * Invalidates on add/delete: same + ["entity", entityId] + ["entities"] + ["changelog-recent"]
 * Invalidates on edit: ["entity-directors", entityId] + ["entity", entityId] + ["entities"]
 *                      + ["changelog-recent"]
 */

import { useState, useId, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { cn } from "../lib/cn";
import {
  fetchDirectors,
  createDirector,
  deleteDirector,
  patchDirector,
} from "../lib/dataFetchers";
import { extractApiError } from "../lib/api";
import { Button } from "./ui/Button";
import { FieldActions } from "./FieldActions";
import { FieldEditHistory } from "./FieldEditHistory";
import { FieldHistoryPopover } from "./FieldHistoryPopover";
import { useFieldHasHistory } from "../lib/EntityHistoryFieldsContext";
import type { Director } from "../lib/types";

// ─── Query invalidation keys ──────────────────────────────────────────────────

function invalidationKeys(entityId: string) {
  // ["renewals"] is intentionally omitted: directors don't feed renewal date derivation,
  // so invalidating that cache on director mutations would be unnecessary churn.
  return [
    { queryKey: ["entity-directors", entityId] },
    { queryKey: ["entity", entityId] },
    { queryKey: ["changelog-recent"] },
    { queryKey: ["entities"] },
  ];
}

function editInvalidationKeys(entityId: string) {
  return [
    { queryKey: ["entity-directors", entityId] },
    { queryKey: ["entity", entityId] },
    { queryKey: ["entities"] },
    { queryKey: ["changelog-recent"] },
  ];
}

// ─── Add director form (inline) ───────────────────────────────────────────────

interface AddDirectorFormProps {
  entityId: string;
  onDone: () => void;
}

function AddDirectorForm({ entityId, onDone }: AddDirectorFormProps): JSX.Element {
  const { t } = useTranslation("directors");
  const qc = useQueryClient();
  const uid = useId();
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [signingAuthority, setSigningAuthority] = useState("");
  const [nameError, setNameError] = useState("");

  const ids = {
    name: `${uid}-director-name`,
    title: `${uid}-director-title`,
    signingAuthority: `${uid}-director-signing-authority`,
    nameError: `${uid}-director-name-err`,
  };

  const mutation = useMutation({
    mutationFn: () =>
      createDirector(entityId, {
        name: name.trim(),
        title: title.trim() || null,
        signingAuthority: signingAuthority.trim() || null,
        comment: null,
      }),
    onSuccess: () => {
      toast.success(t("add_success_toast"));
      invalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      onDone();
    },
    onError: (err) => toast.error(extractApiError(err) || t("add_error_toast")),
  });

  function validate(): boolean {
    if (!name.trim()) {
      setNameError(t("name_required"));
      return false;
    }
    setNameError("");
    return true;
  }

  const canSubmit = name.trim().length > 0 && !mutation.isPending;

  function handleSubmit() {
    if (validate()) mutation.mutate();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.preventDefault(); onDone(); }
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); handleSubmit(); }
  }

  return (
    <div className="border border-accent/20 rounded-[10px] p-4 bg-accent-tint space-y-3 mt-2">
      <p className="text-[10px] font-bold tracking-[.05em] uppercase text-dim">{t("add_director")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label htmlFor={ids.name} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("name_label")} *
          </label>
          <input
            id={ids.name}
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setNameError(""); }}
            onKeyDown={handleKeyDown}
            placeholder={t("name_placeholder")}
            aria-describedby={nameError ? ids.nameError : undefined}
            aria-invalid={nameError ? true : undefined}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            className={cn(
              "h-9 w-full px-3 rounded-lg border bg-surface text-base",
              "focus:ring-2 focus:ring-accent/14 focus:outline-none",
              nameError
                ? "border-red focus:border-red"
                : "border-border focus:border-accent",
            )}
          />
          {nameError && (
            <span id={ids.nameError} role="alert" className="text-[11.5px] text-red-text mt-1 block">
              {nameError}
            </span>
          )}
        </div>
        <div>
          <label htmlFor={ids.title} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("title_label")}
          </label>
          <input
            id={ids.title}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("title_placeholder")}
            className={cn(
              "h-9 w-full px-3 rounded-lg border border-border bg-surface text-base",
              "focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          />
        </div>
        <div>
          <label htmlFor={ids.signingAuthority} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("signing_authority_label")}
          </label>
          <input
            id={ids.signingAuthority}
            type="text"
            value={signingAuthority}
            onChange={(e) => setSigningAuthority(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("signing_authority_placeholder")}
            className={cn(
              "h-9 w-full px-3 rounded-lg border border-border bg-surface text-base",
              "focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
          {mutation.isPending ? "…" : t("save_button")}
        </Button>
        <Button variant="secondary" onClick={onDone}>
          {t("cancel_button")}
        </Button>
      </div>
    </div>
  );
}

// ─── Director row edit form ───────────────────────────────────────────────────

interface DirectorEditFormProps {
  entityId: string;
  director: Director;
  onDone: () => void;
  pencilRef: React.RefObject<HTMLButtonElement | null>;
}

function DirectorEditForm({ entityId, director, onDone, pencilRef }: DirectorEditFormProps): JSX.Element {
  const { t } = useTranslation("directors");
  const qc = useQueryClient();
  const uid = useId();

  const [name, setName] = useState(director.name);
  const [title, setTitle] = useState(director.title ?? "");
  const [signingAuthority, setSigningAuthority] = useState(director.signingAuthority ?? "");
  const [nameError, setNameError] = useState("");

  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const ids = {
    name: `${uid}-edit-director-name`,
    title: `${uid}-edit-director-title`,
    signingAuthority: `${uid}-edit-director-signing-authority`,
    nameError: `${uid}-edit-director-name-err`,
  };

  const mutation = useMutation({
    mutationFn: () =>
      patchDirector(entityId, director.id, {
        name: name.trim(),
        title: title.trim() || null,
        signingAuthority: signingAuthority.trim() || null,
        comment: null,
        expectedUpdatedAt: director.updatedAt,
      }),
    onSuccess: () => {
      toast.success(t("edit_success_toast"));
      editInvalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      onDone();
      pencilRef.current?.focus();
    },
    onError: (err) => {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        toast.error(t("edit_conflict_toast"));
        void qc.invalidateQueries({ queryKey: ["entity-directors", entityId] });
        onDone();
        pencilRef.current?.focus();
      } else {
        toast.error(extractApiError(err) || t("edit_error_toast"));
      }
    },
  });

  function validate(): boolean {
    setNameError("");
    if (!name.trim()) {
      setNameError(t("name_required"));
      return false;
    }
    return true;
  }

  function handleSave() {
    if (validate()) mutation.mutate();
  }

  function handleCancel() {
    onDone();
    pencilRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.preventDefault(); handleCancel(); }
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); handleSave(); }
  }

  return (
    <fieldset
      aria-label={t("edit_director_title")}
      className="border border-accent/20 rounded-[10px] p-4 bg-accent-tint space-y-3 mt-2"
    >
      <legend className="text-[10px] font-bold tracking-[.05em] uppercase text-dim px-0.5">
        {t("edit_director_title")}
      </legend>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label htmlFor={ids.name} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("name_label")} *
          </label>
          <input
            ref={nameRef}
            id={ids.name}
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setNameError(""); }}
            onKeyDown={handleKeyDown}
            aria-describedby={nameError ? ids.nameError : undefined}
            aria-invalid={nameError ? true : undefined}
            className={cn(
              "h-9 w-full px-3 rounded-lg border bg-surface text-base",
              "focus:ring-2 focus:ring-accent/14 focus:outline-none",
              nameError
                ? "border-red focus:border-red"
                : "border-border focus:border-accent",
            )}
          />
          {nameError && (
            <span id={ids.nameError} role="alert" className="text-[11.5px] text-red-text mt-1 block">
              {nameError}
            </span>
          )}
        </div>

        <div>
          <label htmlFor={ids.title} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("title_label")}
          </label>
          <input
            id={ids.title}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("title_placeholder")}
            className={cn(
              "h-9 w-full px-3 rounded-lg border border-border bg-surface text-base",
              "focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          />
        </div>

        <div>
          <label htmlFor={ids.signingAuthority} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">
            {t("signing_authority_label")}
          </label>
          <input
            id={ids.signingAuthority}
            type="text"
            value={signingAuthority}
            onChange={(e) => setSigningAuthority(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("signing_authority_placeholder")}
            className={cn(
              "h-9 w-full px-3 rounded-lg border border-border bg-surface text-base",
              "focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          />
        </div>
      </div>

      <FieldEditHistory entityId={entityId} field="director.name" />
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <Button variant="primary" disabled={mutation.isPending} onClick={handleSave}>
          {mutation.isPending ? "…" : t("edit_save")}
        </Button>
        <Button variant="secondary" onClick={handleCancel}>
          {t("edit_cancel")}
        </Button>
      </div>
    </fieldset>
  );
}

// ─── Director row ─────────────────────────────────────────────────────────────

interface DirectorRowProps {
  entityId: string;
  director: Director;
  canEdit: boolean;
}

function DirectorRow({ entityId, director, canEdit }: DirectorRowProps): JSX.Element {
  const { t } = useTranslation("directors");
  const { t: tAudit } = useTranslation("audit");
  const qc = useQueryClient();
  const hasHistory = useFieldHasHistory("director");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const pencilRef = useRef<HTMLButtonElement>(null);

  const deleteMutation = useMutation({
    mutationFn: () => deleteDirector(entityId, director.id),
    onSuccess: () => {
      toast.success(t("delete_success_toast"));
      invalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      setShowDeleteConfirm(false);
    },
    onError: (err) => toast.error(extractApiError(err) || t("delete_error_toast")),
  });

  const locale = i18n.language;
  const formatDisplayDate = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });

  return (
    <li className="py-[6px] border-b border-border-2 last:border-b-0 group">
      <div className="flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-3">
        <div className="flex-1 min-w-0 space-y-0.5">
          <span
            data-field="director.name"
            className="text-[13.5px] font-semibold text-text block truncate"
          >
            {director.name}
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {director.title ? (
              <span data-field="director.title" className="text-[12px] text-dim truncate max-w-[200px]">
                {director.title}
              </span>
            ) : (
              <span data-field="director.title" className="sr-only">—</span>
            )}
            {director.signingAuthority && (
              <>
                {director.title && <span className="text-dim" aria-hidden>·</span>}
                <span data-field="director.signingAuthority" className="text-[12px] text-dim truncate max-w-[200px]">
                  {director.signingAuthority}
                </span>
              </>
            )}
          </div>
          <p
            data-testid="director-timestamps"
            data-field="director.createdAt"
            className="text-[11px] text-dim"
          >
            {formatDisplayDate(director.createdAt)} · {formatDisplayDate(director.updatedAt)}
          </p>
        </div>

        {canEdit && !showEditForm && (
          <div className="shrink-0 flex items-center gap-1.5">
            <FieldActions
              fieldLabel={t("edit_director_label", { name: director.name })}
              canEdit={true}
              showClock={hasHistory}
              clockTrigger={hasHistory ? (
                <FieldHistoryPopover
                  entityId={entityId}
                  field="director"
                  triggerAriaLabel={tAudit("open_field_history", { field: director.name })}
                />
              ) : undefined}
              onEdit={() => { setShowEditForm(true); setShowDeleteConfirm(false); }}
              editButtonRef={pencilRef}
            />

            {showDeleteConfirm ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate()}
                  className="h-8 px-2.5 rounded-[7px] bg-red text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50 min-w-[60px]"
                >
                  {deleteMutation.isPending ? "…" : t("delete_director")}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="h-8 px-2 rounded-[7px] border border-border text-[12px] text-text hover:bg-surface-2"
                >
                  {t("cancel_button")}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                title={t("delete_director")}
                aria-label={`${t("delete_director")} — ${director.name}`}
                className={cn(
                  "inline-flex items-center justify-center h-8 w-8 rounded-[7px]",
                  "text-dim hover:text-red hover:bg-red-soft",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-red/30",
                )}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>
        )}
      </div>

      {showEditForm && canEdit && (
        <DirectorEditForm
          entityId={entityId}
          director={director}
          onDone={() => setShowEditForm(false)}
          pencilRef={pencilRef}
        />
      )}
    </li>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function DirectorsSkeleton(): JSX.Element {
  return (
    <ul className="divide-y divide-border-2">
      {Array.from({ length: 2 }, (_, i) => (
        <li key={i} className="flex items-center gap-3 py-3">
          <div className="flex-1 space-y-2">
            <div className="skeleton h-4 w-48" />
            <div className="skeleton h-3 w-32" />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface DirectorsEditorProps {
  entityId: string;
  canEdit: boolean;
}

export function DirectorsEditor({ entityId, canEdit }: DirectorsEditorProps): JSX.Element {
  const { t } = useTranslation("directors");
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["entity-directors", entityId],
    queryFn: () => fetchDirectors(entityId),
    enabled: Boolean(entityId),
  });

  const directors = data?.directors ?? [];

  return (
    <div className="space-y-2">
      {/* Section header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[10px] font-bold tracking-[.05em] uppercase text-dim">
          {t("section_title")}
        </p>
        {canEdit && !showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className={cn(
              "text-accent font-semibold text-[13px] inline-flex items-center gap-1.5 py-[7px] cursor-pointer",
              "hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded",
            )}
          >
            <Plus className="h-[14px] w-[14px]" aria-hidden />
            {t("add_director")}
          </button>
        )}
      </div>

      {showAdd && canEdit && (
        <AddDirectorForm entityId={entityId} onDone={() => setShowAdd(false)} />
      )}

      {isError ? (
        <p className="text-[13px] text-muted py-1">{t("load_error")}</p>
      ) : isLoading ? (
        <DirectorsSkeleton />
      ) : directors.length === 0 && !showAdd ? (
        <p className="text-[13.5px] text-dim py-1 italic">{t("no_directors_recorded")}</p>
      ) : (
        <ul className="divide-y divide-border-2">
          {directors.map((director) => (
            <DirectorRow
              key={director.id}
              entityId={entityId}
              director={director}
              canEdit={canEdit}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
